import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { api } from "../../../../api/config";
import { useRecords } from "../../../../api/useRecords";
import { NFB_SERVICE, STALE_TIME_24H } from "../../../../utils/constants";
import { BlockForecast, GapTechDisplay, PriorityBlock, TechRef } from "./types.nfbAssign";

const ANCHOR_FLIP = "awaiting_in_clinic";

const toSlotLabel = (blockTimeRaw: string | null) => {
	const slotLabel = blockTimeRaw ? format(parseISO(blockTimeRaw), "HH:mm") : null;
	return slotLabel;
};

const fetchBlockForecast = async (sessionId: number) => {
	const response = await api({
		method: "POST",
		url: "/gap-allocator/gap-techs-forecast",
		data: { key: sessionId, sessionStatusFlip: ANCHOR_FLIP }
	});

	const forecast: BlockForecast = {
		previousTechs: (response.data?.previousBlock?.techsForGaps ?? []) as Array<TechRef>,
		previousSlotLabel: toSlotLabel(response.data?.previousBlock?.blockTimeRaw ?? null),
		currentTechs: (response.data?.currentBlock?.techsForGaps ?? []) as Array<TechRef>,
		nextTechs: (response.data?.nextBlock?.techsForGaps ?? []) as Array<TechRef>,
		nextSlotLabel: toSlotLabel(response.data?.nextBlock?.blockTimeRaw ?? null)
	};
	return forecast;
};

const toGapTechDisplays = (techs: Array<TechRef>, technicianUserById: Map<number, any>) => {
	const displays: Array<GapTechDisplay> = techs.map((technician) => {
		const user = technicianUserById.get(technician.id);
		return {
			id: technician.id,
			firstName: user?.first_name,
			lastName: user?.last_name,
			avatar: user?.avatar
		};
	});
	return displays;
};

export const useBlockGapTechs = (
	clinicId: number | undefined,
	blocks: Array<PriorityBlock>,
	now: Date,
	isEnabled = true
) => {
	const todayKey = format(now, "yyyy-MM-dd");

	const { data: techniciansData } = useRecords(
		"technicians",
		"id,user_id.id,user_id.first_name,user_id.last_name,user_id.avatar",
		{ extraOptions: { pageSize: -1 }, staleTime: STALE_TIME_24H, enabled: isEnabled }
	);

	const technicianUserById = useMemo(() => {
		const map = new Map<number, any>();
		(techniciansData?.items ?? []).forEach((technician: any) => map.set(technician.id, technician.user_id));
		return map;
	}, [techniciansData]);

	const previousBlock = blocks.find((block) => block.relative === "previous");
	const currentBlock = blocks.find((block) => block.relative === "current");
	const nextBlock = blocks.find((block) => block.relative === "next");

	// the endpoint accepts any NFB-category session (NFB or EEG) as anchor; an NFB one is preferred
	// because it always starts on the grid, but any session of the block must keep the query alive
	const [firstSession] = currentBlock?.sessions ?? [];
	const nfbSession = currentBlock?.sessions.find((session) => session.service?.id === NFB_SERVICE);
	const anchorSessionId = nfbSession?.id ?? firstSession?.id ?? null;

	const forecastQuery = useQuery({
		queryKey: ["gap-techs", "forecast", clinicId, todayKey, currentBlock?.slotLabel ?? "none"],
		queryFn: () => fetchBlockForecast(anchorSessionId!),
		enabled: isEnabled && !!clinicId && anchorSessionId !== null,
		staleTime: 2 * 60 * 1000,
		refetchInterval: 5 * 60 * 1000,
		refetchOnMount: "always" as const,
		refetchOnWindowFocus: true as const
	});

	const gapTechsByBlock = useMemo(() => {
		const byBlock = new Map<string, Array<GapTechDisplay>>();
		const forecast = forecastQuery.data;
		if (!forecast) return byBlock;

		// the anchor is always a session of the current block, so its forecast applies even when the
		// session sits off the NFB grid and the backend floors it to a different slot label
		if (currentBlock) {
			byBlock.set(currentBlock.slotLabel, toGapTechDisplays(forecast.currentTechs, technicianUserById));
		}
		if (previousBlock && forecast.previousSlotLabel === previousBlock.slotLabel) {
			byBlock.set(previousBlock.slotLabel, toGapTechDisplays(forecast.previousTechs, technicianUserById));
		}
		if (nextBlock && forecast.nextSlotLabel === nextBlock.slotLabel) {
			byBlock.set(nextBlock.slotLabel, toGapTechDisplays(forecast.nextTechs, technicianUserById));
		}

		return byBlock;
	}, [previousBlock, currentBlock, nextBlock, technicianUserById, forecastQuery.data]);

	return gapTechsByBlock;
};
